import { useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { HelpCircle } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useCreateObjective, useObjectives } from "@/hooks/use-objectives";
import { useCurrentUser, isSupervisorOrAbove } from "@/hooks/use-current-user";
import { useUsers } from "@/hooks/use-users";
import { AlignmentFields, isParentValidForLevel } from "./alignment-fields";
import { ObjectiveTypeSelect } from "./objective-type-field";
import { FormError } from "@/components/shared/form-error";
import type { ObjectiveLevel } from "@/lib/objective-levels";
import { YEAR_OPTIONS, DEFAULT_YEAR } from "@/lib/year-options";
import { compareUsersByLastName, formatNameLastFirst } from "@/lib/format-name";

const LEVEL_HINTS: Record<ObjectiveLevel, string> = {
  department: "Department OKRs are owned by the AVP and must align to a Strategic Initiative. They set direction for all of ITS.",
  team: "Directors create Team OKRs. Use the Parent Objective field below to link this to one of the AVP's Department OKRs.",
  individual: "Staff create Individual OKRs. Use the Parent Objective field below to link this to your Director's Team OKR.",
};

const createObjectiveSchema = z.object({
  owner_id: z.string().min(1, "Owner is required"),
  title: z.string().min(1, "Title is required").max(500),
  description: z.string().max(5000),
  type: z.enum(["committed", "aspirational"]),
  level: z.enum(["department", "team", "individual"]),
  year: z.number().int().min(2020).max(2100),
});

type CreateObjectiveForm = z.infer<typeof createObjectiveSchema>;

interface CreateObjectiveDialogProps {
  trigger?: React.ReactNode;
}

export function CreateObjectiveDialog({ trigger }: CreateObjectiveDialogProps) {
  const [open, setOpen] = useState(false);
  const { currentUser } = useCurrentUser();
  const createObjective = useCreateObjective();
  const showOwnerPicker = isSupervisorOrAbove(currentUser?.role);
  const { data: usersData } = useUsers(showOwnerPicker ? { limit: 100 } : { limit: 0 });
  const allUsers = usersData?.data ?? [];
  const defaultOwnerId = currentUser?.id ?? "";

  const [selectedLevel, setSelectedLevel] = useState<ObjectiveLevel>("individual");
  const [selectedOwnerId, setSelectedOwnerId] = useState(defaultOwnerId);
  const [parentObjectiveId, setParentObjectiveId] = useState<string | null>(null);
  const [focusAreaId, setFocusAreaId] = useState<string | null>(null);
  const [tacticId, setTacticId] = useState<string | null>(null);

  const { data: allObjectivesData } = useObjectives({});

  const ownerIsDeptHead = showOwnerPicker
    ? (allUsers.find((u) => u.id === selectedOwnerId)?.department_head ?? false)
    : (currentUser?.departmentHead ?? false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<CreateObjectiveForm>({
    resolver: zodResolver(createObjectiveSchema),
    defaultValues: {
      owner_id: defaultOwnerId,
      title: "",
      description: "",
      type: "committed",
      level: "individual" as ObjectiveLevel,
      year: DEFAULT_YEAR,
    },
  });

  const handleLevelChange = useCallback(
    (value: string) => {
      const newLevel = value as ObjectiveLevel;
      setSelectedLevel(newLevel);
      setValue("level", newLevel);
      if (!isParentValidForLevel(parentObjectiveId, newLevel, allObjectivesData?.data ?? [])) {
        setParentObjectiveId(null);
      }
    },
    [parentObjectiveId, allObjectivesData, setValue],
  );

  const handleOwnerChange = useCallback(
    (value: string) => {
      setValue("owner_id", value);
      setSelectedOwnerId(value);
    },
    [setValue],
  );

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      setOpen(isOpen);
      if (!isOpen) {
        reset();
        setSelectedLevel("individual");
        setSelectedOwnerId(defaultOwnerId);
        setParentObjectiveId(null);
        setFocusAreaId(null);
        setTacticId(null);
      }
    },
    [reset, defaultOwnerId],
  );

  const onSubmit = (data: CreateObjectiveForm) => {
    if (!currentUser) return;
    createObjective.mutate(
      {
        title: data.title,
        description: data.description,
        type: data.type,
        level: data.level,
        year: data.year,
        owner_id: data.owner_id,
        parent_objective_ids: parentObjectiveId ? [parentObjectiveId] : [],
        focus_area_ids: focusAreaId ? [focusAreaId] : [],
        tactic_ids: tacticId ? [tacticId] : [],
      },
      { onSuccess: () => handleOpenChange(false) },
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger ?? <Button>New Objective</Button>}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Objective</DialogTitle>
          <DialogDescription>
            Define a new objective. It will be created in draft status.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {showOwnerPicker && (
            <div className="space-y-2">
              <Label>Owner</Label>
              <Select
                defaultValue={defaultOwnerId}
                onValueChange={handleOwnerChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select owner" />
                </SelectTrigger>
                <SelectContent>
                  {[...allUsers].sort(compareUsersByLastName).map((u) => (
                    <SelectItem key={u.id} value={u.id}>{formatNameLastFirst(u.name)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              placeholder="e.g., Improve service reliability"
              {...register("title")}
            />
            <FormError message={errors.title?.message} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Describe the objective and success criteria..."
              rows={3}
              {...register("description")}
            />
            <FormError message={errors.description?.message} />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <ObjectiveTypeSelect
              value={watch("type")}
              defaultValue="committed"
              onChange={(v) => setValue("type", v)}
            />

            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label>Level</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" className="text-[hsl(var(--muted-foreground))] hover:text-foreground">
                      <HelpCircle className="h-3.5 w-3.5" />
                      <span className="sr-only">Level guidance</span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 text-sm" side="right">
                    <p className="mb-2 font-semibold">Who picks each level?</p>
                    <ul className="space-y-2 text-[hsl(var(--muted-foreground))]">
                      <li>
                        <span className="font-medium text-foreground">Department</span> — AVP only. Sets direction for all of ITS; must link to a Strategic Initiative.
                      </li>
                      <li>
                        <span className="font-medium text-foreground">Team</span> — Directors who lead a team. Must link to a Department OKR.
                      </li>
                      <li>
                        <span className="font-medium text-foreground">Individual</span> — Staff reporting to a Director. Must link to a Team OKR.
                      </li>
                    </ul>
                  </PopoverContent>
                </Popover>
              </div>
              <Select
                defaultValue="individual"
                onValueChange={handleLevelChange}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="department">Department</SelectItem>
                  <SelectItem value="team">Team</SelectItem>
                  <SelectItem value="individual">Individual</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                {LEVEL_HINTS[selectedLevel]}
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Year</Label>
            <Select
              value={watch("year").toString()}
              onValueChange={(v) => setValue("year", parseInt(v, 10))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {YEAR_OPTIONS.map((y) => (
                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormError message={errors.year?.message} />
          </div>

          <AlignmentFields
            level={selectedLevel}
            parentObjectiveId={parentObjectiveId}
            focusAreaId={focusAreaId}
            tacticId={tacticId}
            onParentObjectiveIdChange={setParentObjectiveId}
            onFocusAreaIdChange={setFocusAreaId}
            onTacticIdChange={setTacticId}
            isDepartmentHead={ownerIsDeptHead}
          />

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createObjective.isPending}>
              {createObjective.isPending ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
